import React from 'react';
import {
  IonBackButton,
  IonButtons,
  IonButton,
  IonContent,
  IonHeader,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonText,
  IonTitle,
  IonToolbar,
  IonDatetime,
} from '@ionic/react';
import { useForm, Controller } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';
import { useProcessedMaterialDetail, useProcessedMaterials } from '../hooks/useManufacturing';
import { useLanguage } from '../context/LanguageContext';
import LanguageToggle from '../components/LanguageToggle';
import UserMenu from '../components/UserMenu';

interface Params {
  id: string;
}

interface FormValues {
  quantityProduced: number;
  manufacturedAt: string;
}

const ManufactureForm: React.FC = () => {
  const { id } = useParams<Params>();
  const history = useHistory();
  const { t } = useLanguage();
  const { manufactureMutation } = useProcessedMaterials();
  const detailQuery = useProcessedMaterialDetail(id);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      quantityProduced: 0,
      manufacturedAt: new Date().toISOString(),
    },
  });

  if (!id) {
    history.replace('/tabs/manufacturing');
    return null;
  }

  const onSubmit = async (values: FormValues) => {
    if (!values.quantityProduced) return;
    await manufactureMutation.mutateAsync({
      processedMaterialId: id,
      quantityProduced: Number(values.quantityProduced),
      manufacturedAt: values.manufacturedAt,
    });
    history.replace(`/tabs/manufacturing/${id}`);
  };

  const material = detailQuery.data;

  return (
    <IonPage>
      <IonHeader translucent>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref={`/tabs/manufacturing/${id}`} />
          </IonButtons>
          <IonTitle>{t('manufacture')}</IonTitle>
          <LanguageToggle />
          <UserMenu />
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen className="ion-padding">
        {material ? (
          <IonText color="medium">
            <p>
              {material.name} • {material.unit}
            </p>
          </IonText>
        ) : null}
        <form onSubmit={handleSubmit(onSubmit)}>
          <IonList>
            <IonItem>
              <IonLabel position="stacked">{t('manufacturedDate')}</IonLabel>
              <Controller
                control={control}
                name="manufacturedAt"
                rules={{ required: true }}
                render={({ field }) => (
                  <IonDatetime
                    value={field.value}
                    onIonChange={(event) => {
                      const dateValue = Array.isArray(event.detail.value) ? event.detail.value[0] : event.detail.value;
                      field.onChange(dateValue || '');
                    }}
                    presentation="date"
                    max={new Date().toISOString()}
                  />
                )}
              />
            </IonItem>
            <IonItem>
              <IonLabel position="stacked">{t('quantityProduced')}</IonLabel>
              <Controller
                control={control}
                name="quantityProduced"
                rules={{ required: true, min: 0 }}
                render={({ field }) => (
                  <IonInput
                    type="number"
                    value={field.value?.toString() ?? ''}
                    onIonChange={(event) => field.onChange(Number(event.detail.value ?? 0))}
                  />
                )}
              />
              {errors.quantityProduced ? (
                <IonText color="danger">{t('quantityProduced')}</IonText>
              ) : null}
            </IonItem>
          </IonList>

          <IonButton
            expand="block"
            type="submit"
            disabled={isSubmitting}
            style={{ marginTop: 24 }}
          >
            {isSubmitting ? t('saving') ?? 'Saving…' : t('manufacture')}
          </IonButton>
        </form>
      </IonContent>
    </IonPage>
  );
};

export default ManufactureForm;
